import React, { forwardRef, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { LoadingButtonProps } from '@mui/lab/LoadingButton';
import { LoadingButton } from '@mui/lab';
// components
import { CloudDownload } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { formatDate, formatMoney, sanitizeCsvString } from '../../helpers';

export interface ICSVColumn {
  header: string;
  key: string;
  type?: 'date' | 'datetime' | 'currency' | 'list';
  join?: string;
  transform?: (data: any[]) => void;
}
export interface ICSVDownloadButtonProps extends LoadingButtonProps {
  apiRequest: () => Promise<any>;
  transformResponse?: (response: any) => any[];
  handleData?: (data: any[]) => void;
  name: string;
  columns: ICSVColumn[];
  fileNamePrefix: string;
  fileNameSuffix?: string;
}

export const CSVDownloadButton = forwardRef<HTMLButtonElement, ICSVDownloadButtonProps>(
  <C extends React.ElementType>(
    props: LoadingButtonProps<C, { component?: C }>,
    ref?: React.Ref<HTMLButtonElement>
  ) => {
    const {
      apiRequest,
      transformResponse,
      handleData,
      name,
      fileNamePrefix,
      fileNameSuffix,
      columns,
      children = 'CSV',
      id,
      color = 'primary',
      variant = 'contained',
      onClick,
      startIcon = <CloudDownload />,
      loading,
      ...rest
    } = props;

    const { enqueueSnackbar } = useSnackbar();

    const isLoading = useMemo(() => loading ?? false, [loading]); // Loading hook, in case something has to happen before or after CSV download
    const [isDownloading, setIsDownloading] = useState(false);

    const fetchCSVData = async () => {
      try {
        const response = await apiRequest();
        const data = transformResponse ? transformResponse(response) : response;
        handleData && handleData(data); // callback to store data or pass it after formatting
        return data;
      } catch (err) {
        enqueueSnackbar(`Error loading ${name} CSV data`, { variant: 'error' });
      }
    };

    const formatHeaders = () => {
      let headers = '';
      columns.forEach(col => {
        headers += `${col.header},`;
      });
      return `${headers}\n`;
    };

    const formatColumnValue = (col: ICSVColumn, row: any) => {
      const data = !!col.transform ? col.transform(row[col.key]) : row[col.key];
      if (col.type === 'date') {
        return `${formatDate(data)},`;
      }
      if (col.type === 'datetime') {
        const dateTime = new Date(data);
        const date = dateTime.toLocaleDateString('en-US');
        const time = dateTime.toLocaleTimeString('en-US');
        return `${date} ${time},`;
      }
      if (col.type === 'currency') {
        return `"${formatMoney(data, 2)}",`; // "" needed to allow comma within currency
      }
      if (col.type === 'list') {
        return `"${data.join(col.join ?? ', ')}",`;
      }
      const clean = sanitizeCsvString(`${row[col.key]}`);
      return `${clean},`;
    };

    const formatCSVColumns = (data: any[], csv: string) => {
      data.forEach(row => {
        columns.forEach(col => {
          if (!!row[col.key]) {
            csv += formatColumnValue(col, row);
          } else {
            csv += ',';
          }
        });
        csv += '\n';
      });
      return csv;
    };

    const handleFetch = async () => {
      try {
        setIsDownloading(true);
        const csvData = await fetchCSVData();
        const BOM = String.fromCharCode(0xfeff);
        // Initialize CSV with headers
        let csv = formatHeaders();
        // Add values to the csv
        csv = formatCSVColumns(csvData, csv);

        // Create an file the user can download
        const el = document.createElement('a');
        const encodedData = encodeURI(`${BOM}${csv}`);
        const suffix = fileNameSuffix ? `_${fileNameSuffix}` : '';
        el.href = `data:text/csv;charset=UTF-8,${encodedData}`;
        el.target = '_blank';
        el.download = `${fileNamePrefix}_${format(
          parseISO(new Date().toISOString()),
          'yyyy_LL_dd_hh_mm_ss'
        )}${suffix}.csv`;
        // Download the file
        el.click();
      } catch (error) {
        enqueueSnackbar(`Error downloading CSV file: ${JSON.stringify(error)}`, {
          variant: 'error'
        });
      } finally {
        setIsDownloading(false);
      }
    };

    return (
      <LoadingButton
        {...rest}
        loading={isDownloading || isLoading}
        loadingPosition="start"
        color={color}
        variant={variant}
        data-testid={`${id}-csv-download-button`}
        disabled={isLoading || isDownloading || props.disabled ? true : false}
        id={`${id}-csv-download-button`}
        ref={ref}
        startIcon={startIcon}
        onClick={() => {
          handleFetch();
          onClick && onClick();
        }}
      >
        {children}
      </LoadingButton>
    );
  }
);

CSVDownloadButton.displayName = 'CSVDownloadButton';
